'use client';

import { Metadata } from 'next';
import { FAQJsonLd } from '@/components/JsonLd';
import { useState, useEffect } from 'react';
import { ChevronDown, Package, CreditCard, Truck, MapPin, Clock, ShieldCheck, Gift, HelpCircle, Phone, Mail, Store } from 'lucide-react';

// FAQ categories with icons and colors
const categories = [
  { id: 'delivery', name: 'Delivery & Zones', icon: Truck, color: 'blue' },
  { id: 'orders', name: 'Orders & Payments', icon: Package, color: 'purple' },
  { id: 'policies', name: 'Policies & Verification', icon: ShieldCheck, color: 'red' },
  { id: 'discounts', name: 'Discounts & Rewards', icon: Gift, color: 'green' },
  { id: 'events', name: 'Event Planning', icon: Store, color: 'amber' },
  { id: 'contact', name: 'Contact & Support', icon: Phone, color: 'indigo' },
];

// FAQ data organized by category
const faqData = {
  delivery: [
    {
      question: "What are your delivery zones?",
      answer: "We deliver across Manhattan from 12th Street to 74th Street (North-South) and from West River to 3rd Avenue (East-West). Standard delivery takes 40-60 minutes, with express delivery available in 30-40 minutes. Delivery is free for orders over $30. We cannot deliver to public parks, beaches, or office buildings without a doorman. Please check our delivery zone page for complete coverage details."
    },
    {
      question: "How long does delivery take?",
      answer: "Standard delivery takes 40-60 minutes. Express delivery is available in 30-40 minutes for faster service. Delivery times may vary based on order volume and traffic conditions. See our delivery zone page for complete details including delivery hours."
    },
    {
      question: "Do you ship outside NYC?",
      answer: "Currently, we only deliver within the NYC metropolitan area. We do not ship to other states due to varying alcohol shipping regulations."
    },
    {
      question: "What if I'm not home for delivery?",
      answer: "Our delivery team will attempt delivery twice. If you're not available, we'll leave a notice and reschedule. You must be present with valid ID to receive alcohol deliveries. If you are not home for the second delivery attempt, the order will be automatically cancelled and a NON-NEGOTIABLE 15% restocking fee will be charged. This policy is strictly enforced to cover operational costs."
    },
    {
      question: "Is there a minimum order amount?",
      answer: "Yes, we have a $30 minimum order requirement. Orders below $30 will incur a $3.50 small order fee. Delivery is free for all orders over $30 within our delivery zone."
    },
    {
      question: "Can I order through UberEats, DoorDash, or Instacart?",
      answer: "Yes! We're one of the top partners for UberEats, DoorDash, and Instacart. However, we STRONGLY encourage you to place orders directly through our website for better pricing, additional discounts, and accurate inventory availability. While we'll do our best to coordinate orders through third-party platforms, please note that in very rare occasions, there can be discrepancies with pricing and inventory mapping. If we notice any discrepancy that doesn't match our actual price and inventory status, we reserve the right to modify or cancel orders with notification from our internal staff."
    },
  ],
  orders: [
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, MasterCard, American Express), debit cards, and digital wallets including Apple Pay and Google Pay."
    },
    {
      question: "How do I track my order?",
      answer: "You'll receive email and SMS updates with tracking information once your order is confirmed and shipped. You can also track your order through your account dashboard."
    },
    {
      question: "Can I modify or cancel my order?",
      answer: "You can modify or cancel your order within 5 minutes of placing it. After that, your order enters our fulfillment process and cannot be changed. Please contact us immediately at (646) 838-5011 if you need to make changes."
    },
    {
      question: "What are the requirements for pickup orders?",
      answer: "Pickup orders have STRICT verification requirements that are NON-NEGOTIABLE. You MUST present BOTH: (1) A valid, non-expired government-issued photo ID proving you are 21+ years old, AND (2) The original credit card used to place the order. Both items must be presented by the person picking up the order. If you cannot provide both forms of verification, your order will NOT be released. This policy protects against fraud and ensures compliance with New York State alcohol laws. No exceptions will be made under any circumstances."
    },
    {
      question: "Do you offer gift wrapping or gift messages?",
      answer: "Yes! We offer complimentary gift wrapping for orders upon request. You can add a personalized gift message during checkout. Perfect for birthdays, anniversaries, or corporate gifts."
    },
  ],
  policies: [
    {
      question: "Do you require ID verification?",
      answer: "YES - ID verification is MANDATORY and NON-NEGOTIABLE. You must present a valid, non-expired government-issued photo ID (driver's license, passport, or state ID) proving you are 21 years of age or older at the time of delivery. Without proper ID verification, the delivery will be refused. This is a legal requirement under New York State law for all alcohol deliveries."
    },
    {
      question: "Can I return or exchange products?",
      answer: "Due to alcohol regulations, we cannot accept returns of opened bottles. Unopened bottles in original condition can be returned within 7 days of delivery for a full refund."
    },
    {
      question: "What if my order arrives damaged?",
      answer: "If any bottles arrive damaged or broken, please take photos immediately and contact us within 24 hours at (646) 838-5011 or support@amsterwine.com. We will arrange for a replacement or full refund at no additional charge."
    },
    {
      question: "Do you store my payment information?",
      answer: "We use industry-standard encryption and secure payment processing. Your payment information is never stored on our servers - all transactions are processed through secure, PCI-compliant payment gateways."
    },
  ],
  discounts: [
    {
      question: "Do you offer case discounts?",
      answer: "Yes! We offer 10% off for full cases (12+ bottles) and 5% off for half cases (6 bottles) on 750ml and larger bottles. To be eligible for the discount, the case must be comprised of either a full case of wine OR a full case of spirits - you cannot mix wine and spirits to qualify for the case discount. Champagne is excluded as it's already offered at our lowest prices."
    },
    {
      question: "Do you have a rewards or loyalty program?",
      answer: "Yes! We offer a 3% cashback rewards program on all purchases. Points are automatically added to your account and can be redeemed on future orders. Create an account to start earning rewards today."
    },
    {
      question: "Do you offer corporate or bulk discounts?",
      answer: "Yes! We offer special pricing for corporate events, weddings, and bulk orders. Contact us at (646) 838-5011 or support@amsterwine.com to discuss your needs and receive a custom quote."
    },
    {
      question: "Can I use multiple discount codes?",
      answer: "Only one promotional code can be applied per order. Case discounts and rewards points can be combined with promotional codes, but multiple promo codes cannot be stacked."
    },
  ],
  events: [
    {
      question: "Do you offer services for weddings, corporate events, and private parties?",
      answer: "Absolutely! We specialize in providing premium wine and spirits for weddings, corporate events, private parties, and special occasions of all sizes. We offer personalized service, customized pricing, expert sommelier consultations, and delivery coordination. Contact us at (646) 838-5011 or support@amsterwine.com to discuss your event needs."
    },
    {
      question: "Do you accept tax-exempt orders for organizations and event holders?",
      answer: "Yes! We accept tax-exempt orders for qualified organizations, non-profits, educational institutions, and event holders who possess proper tax exemption documentation. You MUST provide a valid, current tax-exempt certificate or form (such as IRS Form 990 or state-issued tax exemption certificate) BEFORE placing your order. Tax exemption will ONLY be applied after verification of proper documentation. Please email your tax-exempt certificate to support@amsterwine.com or contact us at (646) 838-5011 to set up your tax-exempt account."
    },
    {
      question: "What is your pricing structure for large events and bulk orders?",
      answer: "We offer special, customized pricing for large events and bulk orders. Our event pricing is tailored to your specific needs, order size, delivery requirements, and event date. Pricing includes volume discounts beyond our standard case discounts, white-glove delivery service, and dedicated event coordination. For a personalized quote, please contact our event planning team at (646) 838-5011 or support@amsterwine.com with details about your event including date, guest count, beverage preferences, and estimated quantities."
    },
    {
      question: "How far in advance should I place an order for my event?",
      answer: "We recommend placing event orders at least 2-3 weeks in advance for optimal planning and inventory allocation. For large events (100+ guests) or during peak seasons (holidays, wedding season), we recommend 4-6 weeks advance notice. Rush orders may be accommodated based on availability - contact us immediately at (646) 838-5011 to discuss expedited event services."
    },
    {
      question: "Do you offer delivery services for events?",
      answer: "Yes! We provide white-glove delivery services for events. Our team can deliver to your venue and coordinate with event staff for seamless delivery timing. We can also arrange for post-event pickup of unopened products if needed. Delivery timing and pricing are customized based on your venue location and event needs. Please note that we do NOT provide setup services - delivery only. Discuss your delivery requirements when booking your event order."
    },
    {
      question: "Can I return unopened bottles after my event?",
      answer: "Yes! For pre-arranged event orders, we offer a return policy for unopened bottles in original condition. Return terms must be agreed upon BEFORE the event and specified in your event order contract. Please note: If you return more than 30% of the total value of the original purchase, a restocking fee of up to 15% may apply. This policy helps you order confidently without worrying about over-purchasing. Contact us to include return provisions in your event order."
    },
  ],
  contact: [
    {
      question: "What are your business hours?",
      answer: "Store Hours: Sunday 12pm-8:50pm, Monday-Thursday 12pm-9:45pm, Friday-Saturday 11am-10:45pm. Our customer service is available Monday through Friday, 9 AM to 6 PM EST. Online ordering is available 24/7, but delivery scheduling depends on our delivery hours."
    },
    {
      question: "How do I contact you?",
      answer: "You can reach us by phone at (646) 838-5011 or email us at support@amsterwine.com. Visit our store at 475 9th Avenue, Midtown Manhattan, NYC (between 36th & 37th Streets). Store Hours: Sunday 12pm-8:50pm, Monday-Thursday 12pm-9:45pm, Friday-Saturday 11am-10:45pm. Our customer service team is available Monday through Friday, 9 AM to 6 PM EST. You can also visit our contact page for more information."
    },
    {
      question: "Do you offer wine recommendations or consultations?",
      answer: "Absolutely! Our expert sommeliers are available to help you choose the perfect wine for any occasion. You can chat with our AI sommelier on the website, call us at (646) 838-5011, or visit our store for personalized recommendations."
    },
    {
      question: "Can I schedule a delivery for a specific time?",
      answer: "Yes! During checkout, you can select a preferred delivery window. We offer 2-hour delivery windows throughout our operating hours. While we cannot guarantee exact times, we'll do our best to accommodate your preferred timeframe."
    },
  ],
};

// Flatten FAQ data for JSON-LD
const allFaqs = Object.values(faqData).flat();

function FAQAccordionItem({
  question,
  answer,
  isOpen,
  onClick
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors group"
      >
        <h3 className="text-base font-semibold text-gray-900 pr-8 group-hover:text-wine-red transition-colors">
          {question}
        </h3>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180 text-wine-red' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 pb-5 text-gray-600 leading-relaxed">
          {answer}
        </div>
      </div>
    </div>
  );
}

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState<string>('delivery');
  const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});

  // Expand all items on initial load
  useEffect(() => {
    const newOpenItems: { [key: string]: boolean } = {};
    faqData['delivery']?.forEach((_, index) => {
      newOpenItems[`delivery-${index}`] = true;
    });
    setOpenItems(newOpenItems);
  }, []);

  // Expand all items when category changes
  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
    const newOpenItems: { [key: string]: boolean } = {};
    faqData[categoryId as keyof typeof faqData]?.forEach((_, index) => {
      newOpenItems[`${categoryId}-${index}`] = true;
    });
    setOpenItems(newOpenItems);
  };

  const toggleItem = (categoryId: string, index: number) => {
    const key = `${categoryId}-${index}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getCategoryColor = (color: string) => {
    const colors: { [key: string]: string } = {
      blue: 'bg-blue-100 text-blue-600 hover:bg-blue-200',
      purple: 'bg-purple-100 text-purple-600 hover:bg-purple-200',
      red: 'bg-red-100 text-red-600 hover:bg-red-200',
      green: 'bg-green-100 text-green-600 hover:bg-green-200',
      amber: 'bg-amber-100 text-amber-600 hover:bg-amber-200',
      indigo: 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200',
    };
    return colors[color] || colors.blue;
  };

  const getActiveColor = (color: string) => {
    const colors: { [key: string]: string } = {
      blue: 'bg-blue-600 text-white',
      purple: 'bg-purple-600 text-white',
      red: 'bg-red-600 text-white',
      green: 'bg-green-600 text-white',
      amber: 'bg-amber-600 text-white',
      indigo: 'bg-indigo-600 text-white',
    };
    return colors[color] || colors.blue;
  };

  return (
    <>
      <FAQJsonLd questions={allFaqs} />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-wine-red/10 rounded-full mb-4">
              <HelpCircle className="w-8 h-8 text-wine-red" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about ordering, delivery, and our services
            </p>
          </div>

          {/* Side by Side Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Category Navigation - Left Side */}
            <div className="lg:col-span-4 xl:col-span-3">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sticky top-4">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 px-2">
                  Categories
                </h2>
                <div className="space-y-2">
                  {categories.map((category) => {
                    const Icon = category.icon;
                    const isActive = activeCategory === category.id;
                    return (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryChange(category.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 text-left ${
                          isActive
                            ? getActiveColor(category.color) + ' shadow-md'
                            : getCategoryColor(category.color)
                        }`}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm">{category.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* FAQ Content - Right Side */}
            <div className="lg:col-span-8 xl:col-span-9">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-bold text-gray-900">
                    {categories.find(c => c.id === activeCategory)?.name}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {faqData[activeCategory as keyof typeof faqData]?.length} questions
                  </p>
                </div>
                {faqData[activeCategory as keyof typeof faqData]?.map((faq, index) => (
                  <FAQAccordionItem
                    key={`${activeCategory}-${index}`}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={openItems[`${activeCategory}-${index}`] || false}
                    onClick={() => toggleItem(activeCategory, index)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center">
              <Truck className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <div className="text-2xl font-bold text-blue-900 mb-1">40-60 min</div>
              <div className="text-sm text-blue-700">Average Delivery Time</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 text-center">
              <Store className="w-8 h-8 text-purple-600 mx-auto mb-3" />
              <div className="text-2xl font-bold text-purple-900 mb-1">7,000+</div>
              <div className="text-sm text-purple-700">Products Available</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 text-center">
              <Gift className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <div className="text-2xl font-bold text-green-900 mb-1">3% Back</div>
              <div className="text-sm text-green-700">Rewards on Every Order</div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-12 bg-gradient-to-r from-wine-red to-red-700 rounded-xl p-8 text-white shadow-xl">
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Still Have Questions?
              </h2>
              <p className="text-lg mb-6 opacity-90 max-w-2xl mx-auto">
                Our customer service team is here to help you with any questions about our products or services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="/info/contact"
                  className="inline-flex items-center gap-2 bg-white text-wine-red px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  <Mail className="w-5 h-5" />
                  Contact Us
                </a>
                <a
                  href="tel:+16468385011"
                  className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-wine-red transition-all transform hover:-translate-y-0.5"
                >
                  <Phone className="w-5 h-5" />
                  (646) 838-5011
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
